import { CircularLoader } from '@modules/common/components/CircularLoader';
import { useIsArtefactEditable } from '@modules/common/hooks';
import { DasboardHeaderInTVMode } from '@modules/dashboard/components/DasboardHeaderInTVMode/DasboardHeaderInTVMode';
import { DashboardHeader } from '@modules/dashboard/components/DashboardHeader';
import { EmptyDashboard } from '@modules/dashboard/components/EmptyDashboard';
import { GlobalFilters } from '@modules/dashboard/components/GlobalFilters';
import { Layout } from '@modules/dashboard/components/Layout';
import { useUpsertDashboard } from '@modules/dashboard/hooks';
import { useDashboardStore } from '@modules/dashboard/store/useDashboardStore';
import { Box, Paper, Stack } from '@mui/material';
import { cloneDeep, debounce } from 'lodash';
import { useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';

interface DashboardLayoutProps {
  isLoading: boolean;
  shouldDisableInteraction?: boolean;
  shouldHideSaveToDashboardButton?: boolean;
  shouldHideWhereFilter?: boolean;
  shouldHideHeader?: boolean;
  shouldHideGlobalFilters?: boolean;
}

export function DashboardLayout({
  isLoading,
  shouldHideSaveToDashboardButton,
  shouldDisableInteraction = false,
  shouldHideWhereFilter = false,
  shouldHideHeader = false,
  shouldHideGlobalFilters = false,
}: DashboardLayoutProps) {
  const rows = useDashboardStore(({ metadata }) => metadata.layout.rows);
  const editToken = useDashboardStore(({ dashboard }) => dashboard.editToken);
  const status = useDashboardStore(({ dashboard }) => dashboard.status);

  const { upsertDashboard } = useUpsertDashboard();

  const [searchParams] = useSearchParams();
  const shouldShowTVMode = searchParams.get('tv') === 'true';

  const isEditable = useIsArtefactEditable({
    editToken,
    status,
  });

  const onResizeEnd = useMemo(
    () =>
      debounce(() => {
        const dashboard = useDashboardStore.getState().dashboard;

        if (!dashboard.id || !isEditable) {
          return;
        }

        const metadata = cloneDeep(useDashboardStore.getState().metadata);

        upsertDashboard({
          dashboardProps: {
            metadataV2: metadata,
          },
          shouldRefetchAnalyses: false,
        });
      }, 50),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  if (shouldShowTVMode) {
    return (
      <Paper elevation={0} component={Stack} spacing={5} pt={5} px={7}>
        <DasboardHeaderInTVMode />
        <Box>
          <GlobalFilters
            isReadOnly={shouldDisableInteraction}
            shouldHideWhereFilter={shouldHideWhereFilter}
            shouldHideSaveToDashboardButton={shouldHideSaveToDashboardButton}
          />
          <Layout onResizeEnd={onResizeEnd} />
        </Box>
      </Paper>
    );
  }

  const renderContent = () => {
    if (isLoading) {
      return <CircularLoader stackProps={{ height: '500px' }} />;
    }

    if (!rows.length) {
      return <EmptyDashboard />;
    }

    return (
      <Box>
        {shouldHideGlobalFilters ? null : (
          <GlobalFilters
            isReadOnly={shouldDisableInteraction}
            shouldHideWhereFilter={shouldHideWhereFilter}
            shouldHideSaveToDashboardButton={shouldHideSaveToDashboardButton}
          />
        )}
        <Layout
          onResizeEnd={onResizeEnd}
          shouldDisableInteraction={shouldDisableInteraction}
        />
      </Box>
    );
  };

  return (
    <Stack spacing={2.5}>
      {shouldHideHeader ? null : <DashboardHeader />}
      {renderContent()}
    </Stack>
  );
}
